import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { fetchOwners } from 'actions/workflows'
import { isRestrictedFeatureSelector } from 'features/billing'
import { useNewEmptyStates } from 'features/empty_state'
import { useHomeTabs } from 'hooks/home/tabs'
import { useQueryParams } from 'hooks/use_query_params'
import { isAdminOrOwnerSelector } from 'selectors/current_user'
import {
  isWorkflowCollectionEmptySelector,
  firstAndOnlyOwnerSelector, ownerListLoadedSelector
} from 'selectors/workflows'

import { convertScopeType } from '../helpers'
import { useLoadingState } from '../loading_state'
import { OwnerList } from './owner_list'
import { SplashScreen } from './splash_screen'
import { TrialBanner } from './trial_banner'
import { DisabledState as WorkflowsDisabledState } from './workflows_disabled_state'


export const SingleOwnerList = () => {
  const isNewEmptyStateEnabled = useNewEmptyStates()
  const { isLoaded, setLoaded } = useLoadingState()
  const dispatch = useDispatch()
  const { getTabScopeV1 } = useHomeTabs()
  const scope = getTabScopeV1()
  const workflowsExtractionEnabled = useFlag('raven_workflows_extraction')

  const isEmpty = useSelector(isWorkflowCollectionEmptySelector)
  const upgradeIsRequired = useSelector((state) => isRestrictedFeatureSelector(state, { featureKey: 'workflows' }))
  const isOwnerOrAdmin = useSelector(isAdminOrOwnerSelector)
  const ownerListLoaded = useSelector(ownerListLoadedSelector)
  const owner = useSelector(firstAndOnlyOwnerSelector)
  const { admin_managed: adminManaged } = useQueryParams()
  const { can_use_workflows: canUseWorkflows, type: ownerType, id, can_manage_workflows: canManageWorkflows } = owner

  const idWithType = {
    type: ownerType === 'page' ? 'team' : 'user',
    id
  }

  useEffect(() => {
    setLoaded(ownerListLoaded)
  }, [ownerListLoaded, setLoaded])

  useEffect(() => {
    const workflowOwnerType = convertScopeType(scope.type)
    const params = { owner_id: scope.id, owner_type: workflowOwnerType }

    if (adminManaged) {
      params.admin_managed = true
    }

    dispatch(fetchOwners(params, { workflowsExtractionEnabled }))
    return () => {
      dispatch({
        type: 'WORKFLOWS_CLEAR_CREATION_FLAG'
      })
    }
  }, [dispatch, scope.id, scope.type, adminManaged, workflowsExtractionEnabled])

  if (!isLoaded) {
    return null
  }

  if (upgradeIsRequired) {
    const copy = isOwnerOrAdmin ? I18n.t('publisher.workflows.upgrade.message_banner_html')
      : I18n.t('publisher.workflows.contact_admin_for_upgrade.message_banner_html')

    return (<WorkflowsDisabledState bannerCopy={copy} />)
  }

  if (!canUseWorkflows) {
    return (
      <WorkflowsDisabledState
        bannerCopy={I18n.t('publisher.workflows.contact_admin_to_enable.message_banner_html')}
      />
    )
  }


  if (!adminManaged && isEmpty && (ownerType === 'user' || canManageWorkflows)) {
    return (
      <>
        {!isNewEmptyStateEnabled && <TrialBanner />}
        <SplashScreen owner={idWithType} />
      </>
    )
  }

  return (
    <OwnerList />
  )
}
